// storybook-check-ignore
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import * as sessionActions from '@opendoor/shared-fe/build/store/session/actions';
import * as sessionSelectors from '@opendoor/shared-fe/build/store/session/selectors';
import { List, Map } from 'immutable';
import { IRequestStatus } from '../account/RequestStatus';
import { GOOGLE_CLIENT_ID } from '../globals';
import { promptGoogleOneTap as sharedPromptGoogleOneTap } from '@opendoor/shared-fe/build/googleOneTap';
import { analytics } from '@opendoor/observability/slim';
import { createContext, useContext } from 'react';

export interface AccountProps {
  human: Map<string, any>;
  infoIsLoaded: boolean;
  infoIsLoading: boolean;
  leads: List<Map<string, any>>;
  loggedIn: boolean;
  loginRequestStatus: IRequestStatus;
  humanStatusRequestStatus: IRequestStatus;
  registrationRequestStatus: IRequestStatus;
  serverLoginRedirect: string | undefined;
  email: string;
  hasGeneratedPassword: boolean;
  isNewHuman: boolean;
  maybeFetchSessionInfo: () => void;
  maybeFetchSessionLeads: () => void;
  maybeLogOut: () => void;
  logIn: (
    email?: string,
    password?: string,
    googleIDToken?: string,
    facebookIDToken?: string,
  ) => void;
  requestHumanStatus: (email: string) => void;
  register: (
    email?: string,
    name?: string,
    password?: string,
    phone?: string,
    googleIDToken?: string,
    facebookIDToken?: string,
    passwordlessToken?: string,
  ) => void;
  requestResetPassword: (email: string) => void;
}

const mapStateToProps = createSelector(
  [
    sessionSelectors.human,
    sessionSelectors.infoIsLoaded,
    sessionSelectors.infoIsLoading,
    sessionSelectors.leads,
    sessionSelectors.loggedIn,
    sessionSelectors.loginRequestStatus,
    sessionSelectors.humanStatusRequestStatus,
    sessionSelectors.registrationRequestStatus,
    sessionSelectors.serverLoginRedirect,
    sessionSelectors.email,
    sessionSelectors.hasGeneratedPassword,
    sessionSelectors.isNewHuman,
  ],
  (
    human: Map<string, any>,
    infoIsLoaded: boolean,
    infoIsLoading: boolean,
    leads: List<Map<string, any>>,
    loggedIn: boolean,
    loginRequestStatus: IRequestStatus,
    humanStatusRequestStatus: IRequestStatus,
    registrationRequestStatus: IRequestStatus,
    serverLoginRedirect: string | undefined,
    email: string,
    hasGeneratedPassword: boolean,
    isNewHuman: boolean,
  ) => ({
    human,
    infoIsLoaded,
    infoIsLoading,
    leads,
    loggedIn,
    loginRequestStatus,
    humanStatusRequestStatus,
    registrationRequestStatus,
    serverLoginRedirect,
    email,
    hasGeneratedPassword,
    isNewHuman,
  }),
);

const mapDispatchToProps = (dispatch: any) => {
  return {
    maybeFetchSessionInfo: () => {
      dispatch(sessionActions.maybeFetchSessionInfo());
    },
    maybeFetchSessionLeads: () => {
      dispatch(sessionActions.maybeFetchSessionLeads());
    },
    maybeLogOut: () => {
      dispatch(sessionActions.maybeLogOut());
    },
    logIn: (
      email?: string,
      password?: string,
      googleIDToken?: string,
      facebookIDToken?: string,
    ) => {
      dispatch(sessionActions.logIn(email, password, googleIDToken, facebookIDToken));
    },
    requestHumanStatus: (email: string) => {
      dispatch(sessionActions.requestHumanStatus(email));
    },
    requestResetPassword: (email: string) => {
      dispatch(sessionActions.requestPasswordReset(email));
    },
    register: (
      email?: string,
      name?: string,
      password?: string,
      phone?: string,
      googleIDToken?: string,
      facebookIDToken?: string,
      passwordlessToken?: string,
    ) => {
      dispatch(
        sessionActions.register(
          email,
          name,
          password,
          phone,
          googleIDToken,
          facebookIDToken,
          passwordlessToken,
        ),
      );
    },
  };
};

export const AccountConnector = connect(mapStateToProps, mapDispatchToProps);
export default AccountConnector;

export const promptGoogleOneTap = (
  eventLabel: string,
  register: AccountProps['register'],
): void => {
  sharedPromptGoogleOneTap({
    clientId: GOOGLE_CLIENT_ID,
    onSuccess: (response) => {
      analytics.trackEvent('login', 'google-one-tap-success', eventLabel);
      register(undefined, undefined, undefined, undefined, response.credential);
    },
    onPrompted: (isDisplayed, reason) => {
      if (isDisplayed) {
        analytics.trackEvent('login', 'google-one-tap-displayed', eventLabel);
      } else {
        analytics.trackEvent('login', 'google-one-tap-not-displayed', eventLabel, { reason });
      }
    },
    onDismissed: (reason) => {
      analytics.trackEvent('login', 'google-one-tap-dismissed', eventLabel, { reason });
    },
    onSkipped: (reason) => {
      analytics.trackEvent('login', 'google-one-tap-skipped', eventLabel, { reason });
    },
  });
};

export const AccountCtx = createContext<AccountProps>({} as AccountProps);

export const AccountCtxProvider = AccountConnector(
  ({ children, ...accountProps }: AccountProps & { children: React.ReactNode }) => {
    return <AccountCtx.Provider value={accountProps}>{children}</AccountCtx.Provider>;
  },
);

export function useAccountCtx() {
  return useContext(AccountCtx);
}
